import { useMemo, useState } from "react";
import { Box, Button, HStack, Stack, Text, Wrap } from "@chakra-ui/react";
import { PageHeader, SectionCard, DataTable, EmptyState } from "../../../shared/ui";
import { Plus, Users } from "../../../shared/icons";
import { formatarDataHora, parseDataISO } from "../../../shared/lib/date";
import { useAuth } from "../../auth/hooks/useAuth";
import { useAdminUsersQuery } from "../hooks/useAdminUsersQuery";
import { EMPTY_FILTERS, filterUsers } from "../lib/filterUsers";
import UserFilters from "../components/UserFilters";
import UserDetailDrawer from "../components/UserDetailDrawer";
import CreateAccountModal from "../components/CreateAccountModal";
import { AccessBadge, AccountStatusBadge, EmailBadge, RoleBadge } from "../components/UserBadges";

const plural = (n, singular, pluralForm) => `${n} ${n === 1 ? singular : pluralForm}`;

function Medico({ row }) {
  return (
    <Box minW={0}>
      <Text fontWeight="semibold" noOfLines={1}>
        {row.nome || row.email}
      </Text>
      {row.nome ? (
        <Text fontSize="xs" color="app-muted-fg" noOfLines={1}>
          {row.email}
        </Text>
      ) : null}
    </Box>
  );
}

const ultimoAcesso = (row) => (row.last_login_at ? formatarDataHora(parseDataISO(row.last_login_at)) : "Nunca entrou");

// Gestão de usuários (só admin e superadmin): busca, filtros e o detalhe de cada conta
// com as ações. Gestão de CONTAS — não dá acesso a dados financeiros de outro médico.
export default function AdminUsersPage() {
  const { user: viewer } = useAuth();
  const { data: users = [], isLoading } = useAdminUsersQuery();
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(null);
  const [creating, setCreating] = useState(false);

  const rows = useMemo(
    () => filterUsers(users, filters).sort((a, b) => b.id - a.id), // mais recentes primeiro
    [users, filters],
  );

  const detalhes = (row) => (
    <Button size="sm" variant="outline" aria-label={`Detalhes de ${row.email}`} onClick={() => setSelected(row)}>
      Detalhes
    </Button>
  );

  const colunas = [
    { key: "medico", label: "Conta", render: (row) => <Medico row={row} /> },
    { key: "role", label: "Perfil", render: (row) => <RoleBadge role={row.role} /> },
    { key: "acesso", label: "Plano", render: (row) => <AccessBadge row={row} /> },
    { key: "email", label: "E-mail", render: (row) => <EmailBadge verified={row.is_email_verified} /> },
    { key: "ativo", label: "Status", render: (row) => <AccountStatusBadge ativo={row.ativo} /> },
    { key: "last_login_at", label: "Último acesso", render: ultimoAcesso },
    { key: "created_at", label: "Cadastro" },
    { key: "acao", label: "", align: "right", render: detalhes },
  ];

  const renderMobileRow = (row) => (
    <Stack spacing={2} py={3}>
      <HStack justify="space-between" align="flex-start" spacing={3}>
        <Medico row={row} />
        {detalhes(row)}
      </HStack>
      <Wrap spacing={2}>
        <RoleBadge role={row.role} />
        <AccessBadge row={row} />
        <EmailBadge verified={row.is_email_verified} />
        {!row.ativo ? <AccountStatusBadge ativo={false} /> : null}
      </Wrap>
      <Text fontSize="xs" color="app-muted-fg">
        Último acesso: {ultimoAcesso(row)}
      </Text>
    </Stack>
  );

  // a linha selecionada acompanha a listagem atualizada depois de cada ação
  const selectedRow = selected ? users.find((u) => u.id === selected.id) ?? selected : null;

  return (
    <>
      <PageHeader
        title="Usuários"
        description="Gestão de contas do MedStation — não dá acesso a dados financeiros de outros médicos."
        actions={
          <Button leftIcon={<Plus size={16} />} onClick={() => setCreating(true)}>
            Criar conta
          </Button>
        }
      />

      <SectionCard>
        <Stack spacing={4}>
          <UserFilters filters={filters} onChange={setFilters} />
          {!isLoading ? (
            <Text fontSize="xs" color="app-muted-fg">
              {rows.length} de {plural(users.length, "conta", "contas")}
            </Text>
          ) : null}
          <DataTable
            columns={colunas}
            rows={rows}
            isLoading={isLoading}
            renderMobileRow={renderMobileRow}
            emptyState={
              <EmptyState
                icon={Users}
                title="Nenhuma conta encontrada"
                description={users.length ? "Ajuste a busca ou os filtros." : undefined}
              />
            }
          />
        </Stack>
      </SectionCard>

      <UserDetailDrawer row={selectedRow} viewer={viewer} onClose={() => setSelected(null)} />
      <CreateAccountModal isOpen={creating} viewer={viewer} onClose={() => setCreating(false)} />
    </>
  );
}
